import { v4 as uuid } from "uuid";
import type { MarlinStoreDb } from "../infra/db";
import { replaceScreenLayouts } from "../screens/store";
import { withState } from "../infra/state";
import type { TemplateLoad, TemplateRecord } from "../types";

const KEYS = [
  "screenLayouts",
  "multiScreenLayoutTemplates",
  "selectedMultiScreenTemplateId",
  "selectedScreenTemplateIds",
] as const;

const TEMPLATE_LOAD_KEY = "templateLoad";

export const recordTemplateLoad = (db: MarlinStoreDb, templateId: string) =>
  db.settings.put({
    key: TEMPLATE_LOAD_KEY,
    value: { id: uuid(), templateId } satisfies TemplateLoad,
  });

export const getLastTemplateLoad = async (
  db: MarlinStoreDb,
): Promise<TemplateLoad | null> => {
  const row = await db.settings.get(TEMPLATE_LOAD_KEY);
  return (row?.value as TemplateLoad | undefined) ?? null;
};

export const getMultiScreenLayoutTemplates = (
  db: MarlinStoreDb,
): Promise<TemplateRecord[]> => db.templates.toArray();

export const getMultiScreenLayoutTemplate = async (
  db: MarlinStoreDb,
  templateId: string,
): Promise<TemplateRecord> => {
  const template = await db.templates.get(templateId);
  if (!template) throw new Error("Template not found");
  return template;
};

export const saveMultiScreenLayoutTemplate = (
  db: MarlinStoreDb,
  name: string,
): Promise<TemplateRecord> => {
  if (!name || typeof name !== "string")
    throw new Error("Template name is required");
  return withState(db, KEYS, (store) => {
    const template: TemplateRecord = {
      id: uuid(),
      name: name.trim(),
      createdAt: new Date(),
      screenLayouts: structuredClone(store.get("screenLayouts")),
    };
    store.set("multiScreenLayoutTemplates", [
      ...store.get("multiScreenLayoutTemplates"),
      template,
    ]);
    store.set("selectedMultiScreenTemplateId", template.id);
    return template;
  });
};

export const deleteMultiScreenLayoutTemplate = (
  db: MarlinStoreDb,
  templateId: string,
): Promise<{ success: true }> =>
  withState(
    db,
    ["multiScreenLayoutTemplates", "selectedMultiScreenTemplateId"],
    (store) => {
      const templates = store.get("multiScreenLayoutTemplates");
      const remaining = templates.filter((t) => t.id !== templateId);
      if (remaining.length === templates.length)
        throw new Error("Template not found");
      if (store.get("selectedMultiScreenTemplateId") === templateId)
        store.set("selectedMultiScreenTemplateId", null);
      store.set("multiScreenLayoutTemplates", remaining);
      return { success: true as const };
    },
  );

export const loadMultiScreenLayoutTemplate = (
  db: MarlinStoreDb,
  templateId: string,
): Promise<{ success: true }> =>
  withState(db, [...KEYS, "machinesLoaded"], async (store) => {
    const template = store
      .get("multiScreenLayoutTemplates")
      .find((t) => t.id === templateId);
    if (!template) throw new Error("Template not found or failed to load");
    const live = store.get("screenLayouts");
    const byId = new Map(
      template.screenLayouts.map((layout) => [layout.id, layout]),
    );
    replaceScreenLayouts(
      store,
      live.map((layout) => structuredClone(byId.get(layout.id) ?? layout)),
    );
    store.set("selectedMultiScreenTemplateId", templateId);
    store.set("selectedScreenTemplateIds", {});
    await recordTemplateLoad(db, templateId);
    return { success: true as const };
  });

export const getSelectedTemplateId = async (
  db: MarlinStoreDb,
): Promise<string | null> =>
  ((await db.settings.get("selectedMultiScreenTemplateId"))?.value as
    | string
    | undefined) ?? null;

export const setSelectedTemplateId = async (
  db: MarlinStoreDb,
  templateId: string | null,
): Promise<{ success: true }> => {
  if (templateId !== null && typeof templateId !== "string")
    throw new Error("Template ID must be a string or null");
  await db.settings.put({
    key: "selectedMultiScreenTemplateId",
    value: templateId,
  });
  return { success: true };
};
